import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getLeague, updateLeague, deleteLeague, updateProfile } from '../services/api';
import Loading from '../components/Loading';
import getErrorMessage from '../utils/errorHandler';
import { HIDE_MONEY } from '../utils/platform';
import '../styles/app/forms.css';

function PoolSettings() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [pool, setPool] = useState(null);
  const isSetupMode = searchParams.get('setup') === 'true';
  const [settings, setSettings] = useState({
    name: '',
    description: '',
    entry_fee: 10,
    fight_inclusion: 'all', // 'main', 'main_prelims', or 'all'
    method_required_for: 'main', // 'main', 'main_prelims', or 'all'
    method_bonus_points: 3, // bonus points for correct method
    championship_multiplier: 1.5,
    payout_first: 60,
    payout_second: 30,
    payout_third: 10
  });
  const [chargeEntryFee, setChargeEntryFee] = useState(false);
  const [venmoHandle, setVenmoHandle] = useState('');
  const [settingsError, setSettingsError] = useState('');
  const [settingsSuccess, setSettingsSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPool();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Convert legacy include_all_fights to fight_inclusion
  const normalizeFightInclusion = (data) => {
    if (data.fight_inclusion) return data.fight_inclusion;
    // Legacy: 0 = main only, 1 = all
    if (data.include_all_fights === 0 || data.include_all_fights === false) return 'main';
    return 'all';
  };

  const fetchPool = async () => {
    try {
      const response = await getLeague(id);
      setPool(response.data);
      setSettings({
        name: response.data.name ?? '',
        description: response.data.description ?? '',
        entry_fee: response.data.entry_fee ?? 10,
        fight_inclusion: normalizeFightInclusion(response.data),
        method_required_for: response.data.method_required_for ?? 'main',
        method_bonus_points: response.data.method_bonus_points ?? 3,
        championship_multiplier: response.data.championship_multiplier ?? 1.5,
        payout_first: response.data.payout_first ?? 60,
        payout_second: response.data.payout_second ?? 30,
        payout_third: response.data.payout_third ?? 10
      });
      setChargeEntryFee((response.data.entry_fee ?? 0) > 0);
      const commissioner = response.data.members?.find(member => member.id === response.data.commissioner_id);
      setVenmoHandle(commissioner?.venmo_handle || '');
    } catch (error) {
      console.error('Failed to load pool settings:', error);
      navigate(`/pool/${id}`);
    } finally {
      setLoading(false);
    }
  };

  const isCommissioner = pool?.commissioner_id === user?.id;

  const handlePayoutChange = (field, value) => {
    const newValue = Math.max(0, Math.min(100, parseFloat(value) || 0));
    const newSettings = { ...settings, [field]: newValue };

    // Auto-adjust 3rd place to make total 100%
    if (field === 'payout_first') {
      const remaining = 100 - newValue - newSettings.payout_second;
      newSettings.payout_third = Math.max(0, remaining);
    } else if (field === 'payout_second') {
      const remaining = 100 - newSettings.payout_first - newValue;
      newSettings.payout_third = Math.max(0, remaining);
    } else {
      // If changing 3rd, adjust 2nd
      const remaining = 100 - newSettings.payout_first - newValue;
      newSettings.payout_second = Math.max(0, remaining);
    }

    setSettings(newSettings);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSettingsError('');
    setSettingsSuccess('');

    setSaving(true);
    try {
      const nextVenmo = venmoHandle.trim();
      if (!HIDE_MONEY && nextVenmo && user?.venmo_handle !== nextVenmo) {
        await updateProfile({ venmoHandle: nextVenmo });
      }
      const updates = {
        name: settings.name,
        description: settings.description,
        fightInclusion: settings.fight_inclusion,
        methodRequiredFor: settings.method_required_for,
        methodBonusPoints: settings.method_bonus_points,
        championshipMultiplier: settings.championship_multiplier
      };
      // Native hides the money fields, so their keys are omitted entirely:
      // PUT /leagues/:id only updates keys that are present, which leaves the
      // stored entry fee and payout split exactly as they are.
      if (!HIDE_MONEY) {
        Object.assign(updates, {
          entryFee: settings.entry_fee,
          payoutFirst: settings.payout_first,
          payoutSecond: settings.payout_second,
          payoutThird: settings.payout_third
        });
      }
      await updateLeague(id, updates);
      await fetchPool();
      setSettingsSuccess('Settings saved successfully!');
      // Auto-clear success message after 3 seconds
      setTimeout(() => setSettingsSuccess(''), 3000);
    } catch (error) {
      console.error('Failed to update pool settings:', error);
      setSettingsError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePool = async () => {
    if (!window.confirm('Are you sure you want to delete this pool? This cannot be undone.')) return;
    try {
      await deleteLeague(id);
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to delete pool:', error);
    }
  };

  if (loading) return <Loading />;

  if (!isCommissioner) {
    return (
      <div className="container pnp-form-page">
        <Link to={`/pool/${id}`} className="pnp-form-back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6" /></svg>
          <span>Back to Pool</span>
        </Link>
        <div className="pnp-empty">
          <div className="pnp-empty-icon" aria-hidden="true">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
          </div>
          <h3 className="pnp-empty-title">Commissioner Access Only</h3>
          <p>
            Pool settings are only available to the commissioner.
          </p>
        </div>
      </div>
    );
  }

  const handleFinishSetup = () => {
    setSearchParams({});
    navigate(`/pool/${id}`);
  };

  const fightOptions = [
    { value: 'main', label: 'Main card only' },
    { value: 'main_prelims', label: 'Main + Prelims' },
    { value: 'all', label: 'All fights' },
  ];
  const methodOptions = [{ value: 'none', label: 'None' }, ...fightOptions];
  const bonusOptions = [0, 1, 2, 3, 4, 5];
  const multiplierOptions = [
    { value: 1, label: '1x' },
    { value: 1.5, label: '1.5x' },
    { value: 2, label: '2x' },
    { value: 2.5, label: '2.5x' },
    { value: 3, label: '3x' },
  ];

  return (
    <div className="container pnp-form-page pnp-form-page-wide">
      <Link to={`/pool/${id}`} className="pnp-form-back">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6" /></svg>
        <span>Back to {pool?.name}</span>
      </Link>

      {isSetupMode && (
        <div className="pnp-form-setup pnp-rise">
          <span className="pnp-claw" aria-hidden="true" />
          <p className="pnp-eyebrow">Setup</p>
          <h2 className="pnp-form-setup-title">Pool <span className="pnp-punch">Created!</span></h2>
          <p>
            Your pool <strong>{pool?.name}</strong> is ready. Before inviting members, take a moment to configure your settings below.
          </p>
          <div className="pnp-form-checklist">
            <p className="pnp-form-checklist-title">Quick setup checklist:</p>
            <ul>
              {!HIDE_MONEY && <li>Add your Venmo handle so members can pay you</li>}
              {!HIDE_MONEY && <li>Confirm your entry fee and payout structure</li>}
              <li>Choose whether to include all fights or main card only</li>
              {HIDE_MONEY && <li>Set how method picks and title fights score</li>}
            </ul>
          </div>
        </div>
      )}

      <header className="pnp-page-head pnp-rise" style={{ '--i': 1 }}>
        <p className="pnp-eyebrow">Commissioner</p>
        <h1 className="pnp-title">
          {isSetupMode ? <>Configure Your <span className="pnp-punch">Pool</span></> : <>Pool <span className="pnp-punch">Settings</span></>}
        </h1>
        <p className="pnp-page-sub">
          {isSetupMode
            ? 'Review and adjust these settings, then invite your friends!'
            : HIDE_MONEY
              ? 'Configure picks and scoring for this pool.'
              : 'Configure picks, payouts, and entry fee for this pool.'}
        </p>
      </header>

      <form onSubmit={handleSave} className="pnp-form-stack">
        {settingsError && (
          <div className="message message-error" role="alert">
            {settingsError}
          </div>
        )}
        {settingsSuccess && (
          <div className="message message-success" role="status">
            {settingsSuccess}
          </div>
        )}

        <div className="pnp-form-cols">
          <div className="pnp-form-col">
            <section className="pnp-form-section pnp-rise" style={{ '--i': 2 }}>
              <div className="pnp-form-section-head">
                <span className="pnp-form-step">01</span>
                <div>
                  <h2 className="pnp-form-section-title">The basics</h2>
                  <p className="pnp-form-section-sub">What members see at the top of the pool.</p>
                </div>
              </div>
              <div className="pnp-form-field">
                <label className="form-label" htmlFor="ps-name">Pool Name</label>
                <input
                  id="ps-name"
                  type="text"
                  className="form-input"
                  value={settings.name}
                  onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                  required
                />
              </div>
              <div className="pnp-form-field">
                <label className="form-label" htmlFor="ps-desc">Description (optional)</label>
                <textarea
                  id="ps-desc"
                  className="form-input"
                  value={settings.description}
                  onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                  rows={3}
                  placeholder="A brief description of your pool..."
                />
              </div>
            </section>

            {!HIDE_MONEY && (
            <section className="pnp-form-section pnp-rise" style={{ '--i': 3 }}>
              <div className="pnp-form-section-head">
                <span className="pnp-form-step">02</span>
                <div>
                  <h2 className="pnp-form-section-title">Entry fee &amp; payouts</h2>
                  <p className="pnp-form-section-sub">How members pay you and how the pot splits.</p>
                </div>
              </div>
              <div className="pnp-form-field">
                <label className="form-label" htmlFor="ps-venmo">Commissioner Venmo</label>
                <input
                  id="ps-venmo"
                  type="text"
                  className="form-input"
                  placeholder="@yourhandle"
                  value={venmoHandle}
                  onChange={(e) => setVenmoHandle(e.target.value)}
                />
                <p className="pnp-form-help">
                  This is shown on the pool page for member payments.
                </p>
              </div>
              <div className="pnp-form-field">
                <span className="form-label">Entry Fee</span>
                <label className="pnp-form-toggle">
                  <div
                    className={`pnp-form-switch${chargeEntryFee ? ' is-on' : ''}`}
                    onClick={() => {
                      const newVal = !chargeEntryFee;
                      setChargeEntryFee(newVal);
                      if (!newVal) setSettings({ ...settings, entry_fee: 0 });
                      else if (settings.entry_fee === 0) setSettings({ ...settings, entry_fee: 10 });
                    }}
                  >
                    <div className="pnp-form-switch-knob" />
                  </div>
                  <span>
                    Charge entry fee per card
                  </span>
                </label>
                {chargeEntryFee && (
                  <div className="pnp-form-reveal pnp-form-adorn has-pre pnp-form-narrow">
                    <span className="pnp-form-adorn-pre" aria-hidden="true">$</span>
                    <input
                      type="number"
                      className="form-input"
                      value={settings.entry_fee}
                      min="1"
                      step="1"
                      onChange={(e) => setSettings({ ...settings, entry_fee: parseFloat(e.target.value) || 0 })}
                      aria-label="Entry fee"
                    />
                  </div>
                )}
              </div>
              <div className="pnp-form-field">
                <span className="form-label">Payout Structure (%)</span>
                <p className="pnp-form-help pnp-form-help-top">
                  Total: <span className="pnp-form-total">{settings.payout_first + settings.payout_second + settings.payout_third}%</span> (auto-adjusts to 100%)
                </p>
                <div className="pnp-form-payouts">
                  <div className="pnp-form-payout">
                    <label className="pnp-form-place pnp-form-place-1" htmlFor="ps-p1">
                      1st Place
                    </label>
                    <div className="pnp-form-adorn has-suf">
                      <input
                        id="ps-p1"
                        type="number"
                        className="form-input"
                        min="0"
                        max="100"
                        value={settings.payout_first}
                        onChange={(e) => handlePayoutChange('payout_first', e.target.value)}
                      />
                      <span className="pnp-form-adorn-suf">%</span>
                    </div>
                  </div>
                  <div className="pnp-form-payout">
                    <label className="pnp-form-place pnp-form-place-2" htmlFor="ps-p2">
                      2nd Place
                    </label>
                    <div className="pnp-form-adorn has-suf">
                      <input
                        id="ps-p2"
                        type="number"
                        className="form-input"
                        min="0"
                        max="100"
                        value={settings.payout_second}
                        onChange={(e) => handlePayoutChange('payout_second', e.target.value)}
                      />
                      <span className="pnp-form-adorn-suf">%</span>
                    </div>
                  </div>
                  <div className="pnp-form-payout">
                    <label className="pnp-form-place pnp-form-place-3" htmlFor="ps-p3">
                      3rd Place
                    </label>
                    <div className="pnp-form-adorn has-suf">
                      <input
                        id="ps-p3"
                        type="number"
                        className="form-input"
                        min="0"
                        max="100"
                        value={settings.payout_third}
                        onChange={(e) => handlePayoutChange('payout_third', e.target.value)}
                      />
                      <span className="pnp-form-adorn-suf">%</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            )}
          </div>

          <div className="pnp-form-col">
            <section className="pnp-form-section pnp-rise" style={{ '--i': 4 }}>
              <div className="pnp-form-section-head">
                <span className="pnp-form-step">{HIDE_MONEY ? '02' : '03'}</span>
                <div>
                  <h2 className="pnp-form-section-title">Scoring rules</h2>
                  <p className="pnp-form-section-sub">Which fights count and how points are earned.</p>
                </div>
              </div>
              <div className="pnp-form-field">
                <span className="form-label" id="ps-fights">Fight Selection</span>
                <p className="pnp-form-help pnp-form-help-top">
                  Choose which fights members must pick for each event.
                </p>
                <div className="pnp-segmented pnp-form-seg" role="radiogroup" aria-labelledby="ps-fights">
                  {fightOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={settings.fight_inclusion === opt.value}
                      className={settings.fight_inclusion === opt.value ? 'active' : ''}
                      onClick={() => setSettings({ ...settings, fight_inclusion: opt.value })}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="pnp-form-field">
                <span className="form-label" id="ps-method">Method of Victory Required For</span>
                <p className="pnp-form-help pnp-form-help-top">
                  Choose which fights require a method of victory prediction (bonus points).
                </p>
                <div className="pnp-segmented pnp-form-seg" role="radiogroup" aria-labelledby="ps-method">
                  {methodOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={settings.method_required_for === opt.value}
                      className={settings.method_required_for === opt.value ? 'active' : ''}
                      onClick={() => setSettings({ ...settings, method_required_for: opt.value })}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="pnp-form-field">
                <span className="form-label" id="ps-bonus">Method Bonus Points</span>
                <p className="pnp-form-help pnp-form-help-top">
                  Bonus points awarded for correctly predicting the method of victory.
                </p>
                <div className="pnp-segmented pnp-form-seg pnp-form-seg-num" role="radiogroup" aria-labelledby="ps-bonus">
                  {bonusOptions.map((n) => (
                    <button
                      key={n}
                      type="button"
                      role="radio"
                      aria-checked={settings.method_bonus_points === n}
                      className={settings.method_bonus_points === n ? 'active' : ''}
                      onClick={() => setSettings({ ...settings, method_bonus_points: n })}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
              <div className="pnp-form-field">
                <span className="form-label" id="ps-mult">Championship Fight Multiplier</span>
                <p className="pnp-form-help pnp-form-help-top">
                  Points multiplier for title fights.
                </p>
                <div className="pnp-segmented pnp-form-seg pnp-form-seg-num" role="radiogroup" aria-labelledby="ps-mult">
                  {multiplierOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={Number(settings.championship_multiplier) === opt.value}
                      className={Number(settings.championship_multiplier) === opt.value ? 'active' : ''}
                      onClick={() => setSettings({ ...settings, championship_multiplier: opt.value })}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </section>
          </div>
        </div>

        <div className="pnp-form-confirm pnp-form-actions">
          <button className="btn btn-primary btn-lg" type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
          {isSetupMode && (
            <button
              type="button"
              className="btn btn-secondary btn-lg"
              onClick={handleFinishSetup}
            >
              Finish Setup & Invite Members
            </button>
          )}
        </div>
      </form>

      {!isSetupMode && (
      <div className="pnp-form-danger">
        <div className="pnp-form-danger-copy">
          <p className="pnp-eyebrow">Danger Zone</p>
          <h3 className="pnp-form-danger-title">Delete this pool</h3>
          <p>
            Deleting the pool will remove all members and their picks. This action cannot be undone.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-danger"
          onClick={handleDeletePool}
        >
          Delete Pool
        </button>
      </div>
      )}
    </div>
  );
}

export default PoolSettings;
